import React from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod/v3';
import { zodResolver } from '@hookform/resolvers/zod';
import { isValid, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { relationTypes } from 'Modules/financing/config/relationTypes';
export const getSocialSecurityNumberRegex = () => /^\d{3}-?\d{2}-?\d{4}$/;

export const validateISODateFormat = (val) => {
  return val ? isValid(parseISO(val)) : true;
};

const relationValues = relationTypes.map((item) => item.value);
const useCreateApplicationForm = () => {
  const { t } = useTranslation();

  const schema = React.useMemo(() => {
    return z.object({
      // salutation: z.string().min(1,{ message: t('common.requiredSalutation') }),
      firstName: z.string().min(1, { message: t('common.requiredFieldFirstName') }),
      lastName: z.string().min(1, { message: t('common.requiredFieldLastName') }),
      address: z
        .string()
        .min(1, { message: t('common.requiredFieldStreet') })
        .max(35, { message: 'Address must be 35 characters or less' }),
      city: z.string().min(1, { message: t('common.requiredFieldCity') }),
      state: z.string().min(1, { message: t('common.requiredFieldState') }),
      zip: z
        .string()
        .min(1, { message: t('common.requiredFieldPostalCode') })
        .regex(/^\d{5}(-?\d{4})?$/, { message: 'Please provide a valid US ZIP code' }),
      relationship: z.enum(relationValues, {
        errorMap: () => {
          return { message: 'relationship is required' };
        },
      }),
      // country: z.string().min(1,{ message: t('common.requiredFieldCountry') }),
      email: z
        .string()
        .min(1, { message: t('common.requiredFieldEmail') })
        .email({ message: t('common.invalidFormatEmail') })
        .refine((val) => !val.endsWith('@hear.com'), {
          // Custom domain validation
          message: t('common.invalidEmailDomain'), // Custom error message
        }),
      socialSecurityNumber: z
        .string()
        .min(1, { message: t('common.requiredFieldSSN') })
        .refine(
          (val) => {
            const regex = getSocialSecurityNumberRegex();
            return regex.test(val);
          },
          { message: t('common.invalidFormatSSN') }
        ),
      phone: z
        .string()
        .min(1, { message: t('common.requiredFieldPhone') })
        .regex(/^\d{10}$/, { message: 'Please provide a valid phone number with 10 digits' }),
      dateOfBirth: z
        .any()
        .refine(validateISODateFormat, { message: t('common.invalidDateFormat') }),
      annualIncome: z
        .string()
        .transform(parseInt)
        .refine((val) => val >= 1, { message: t('common.requiredFieldAnnualIncome') }),
      terms: z.boolean().refine((val) => val, { message: t('common.requiredFieldTerms') }),
    });
  }, [t]);

  const form = useForm({
    resolver: zodResolver(schema),
  });

  // Validate the address field as the user types (other fields validate on submit)
  React.useEffect(() => {
    const subscription = form.watch((_value, { name }) => {
      if (name === 'address') {
        form.trigger('address');
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return form;
};

export default useCreateApplicationForm;
